'use client';

import React from "react";
import Image from 'next/image';
import { Button } from "@nextui-org/button";
import { Card, CardBody, CardFooter } from "@nextui-org/card";

// Updated interface to match Prisma schema structure
export interface CarouselItemProps {
  carousel: {
    id: string;
    slug: string;
    destination?: string;
    type: string;
    items: Array<{
      id: string;
      imageUrl: string;
      sortOrder: number;
      isActive: boolean;
      translations: Array<{
        id: string;
        lang: string;
        caption?: string;
        link?: string;
        header?: string;
        subheader?: string;
        fullDescription?: string;
        buttonText1?: string;
        buttonLink1?: string;
        buttonText2?: string;
        buttonLink2?: string;
      }>;
    }>;
  };
  dictionary: any;
  lang: string;
}

const CarouselMiddle = ({ carousel, lang }: CarouselItemProps) => {
  // Filter items to only active ones
  const activeItems = carousel.items.filter((item) => item.isActive);

  const getTranslation = (item: typeof activeItems[number]) => {
    const translation = item.translations.find((t) => t.lang === lang);
    return translation || item.translations[0] || null;
  };

  if (activeItems.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-500 dark:text-gray-400">No active items available</p>
      </div>
    );
  }

  return (    
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mt-8 grid auto-rows-fr grid-cols-1 gap-6 sm:mt-12 sm:grid-cols-2 xl:mt-16 xl:grid-cols-3">
          {activeItems.map((item) => {
            const translation = getTranslation(item);
            const title = translation?.header || translation?.caption || '';

            return (
              <Card
                key={item.id}
                isHoverable
                className="group relative flex min-h-[360px] flex-col overflow-hidden rounded-2xl border border-white/10 bg-gray-900/60 shadow-large backdrop-blur-sm transition-transform hover:-translate-y-1 dark:bg-gray-900/80 sm:min-h-[420px] xl:min-h-[480px]"
              >
                <div className="absolute inset-0">
                  <Image
                    alt={title || 'Carousel image'}
                    src={item.imageUrl}
                    fill
                    sizes="(min-width: 1280px) 33vw, (min-width: 640px) 50vw, 100vw"
                    className="object-cover"
                    quality={60}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/10" />
                </div>

                <CardBody className="relative mt-auto flex h-full flex-col justify-end gap-4 p-6 sm:p-7">
                  {title && (
                    <h3 className="text-lg font-semibold leading-snug text-white sm:text-xl">
                      {title}
                    </h3>
                  )}

                  {translation?.subheader && (
                    <p className="text-sm text-gray-200/90 line-clamp-3 sm:text-base">
                      {translation.subheader}
                    </p>
                  )}
                </CardBody>

                {(translation?.buttonText1 || translation?.buttonText2) && (
                  <CardFooter className="flex flex-wrap items-center justify-center gap-3 border-t border-white/10 bg-black/40 px-6 py-6 backdrop-blur sm:px-7">
                    {translation?.buttonText1 && translation?.buttonLink1 && (
                      <Button
                        className="px-4"
                        as="a"
                        href={translation.buttonLink1}
                        color="primary"
                        radius="lg"
                        size="sm"
                      >
                       {translation.buttonText1}
                      </Button>
                    )}

                    {translation?.buttonText2 && translation?.buttonLink2 && (
                      <Button
                        as="a"
                        href={translation.buttonLink2}
                        variant="bordered"
                        radius="lg"
                        size="sm"
                        className="border-white/40 text-white hover:border-white hover:bg-white/10"
                      >
                        {translation.buttonText2}
                      </Button>
                    )}
                  </CardFooter>
                )}
              </Card>
            );
          })}
        </div>
      </div>    
  );
};

export default CarouselMiddle;